import { type FC, type ReactNode, useCallback, useMemo } from "react";
import { DEFAULT_PERSISTED_MONTHLY_SETTINGS, DEFAULT_WEEKLY_SETTINGS } from "@/components/staffing/ScheduleExport/consts";
import { DEFAULT_PERSISTED_EXPORT_SETTINGS } from "@/downloads/weekStaffScheduleExport/persistedSettings";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useUpdateUserSettings, useUserSettings } from "@/hooks/useUserSettings";
import { trackEvent } from "@/utils/analytics";
import { normalizeHppdSeparation } from "@/utils/hppdSeparation";
import {
	DailyPrintoutGroupingOption,
	DailyViewOption,
	HppdSeparationType,
	LocationMode,
	type OtMode,
	PrintoutOrientation,
	type PrintoutShowAdminsConfig,
	type StaffingSettings,
	type StatsMode,
	type UpdateUserSettingsRequest,
	WeeklyGroupingType,
	WeeklyViewOption,
} from "@/utils/models";
import { FEATURE_FLAGS } from "../FeatureFlagsProvider";
import { UserSettingsContext } from "./UserSettingsContext";

export const DEFAULT_SHOW_ADMINS: PrintoutShowAdminsConfig = { enabled: false, admins: [], startColumn: 1, pairSpacing: 1 };

// Scale-to-fit (Excel page setup) defaults — shared so the settings provider and usePrintout agree.
export const DEFAULT_PRINTOUT_FIT_TO_PAGE = true;
export const DEFAULT_PRINTOUT_FIT_TO_WIDTH = 1; // fit all columns to 1 page wide
export const DEFAULT_PRINTOUT_FIT_TO_HEIGHT = 0; // 0 = auto (allow multiple pages tall)

// DEFAULT: Summary-mode (BIPA) printout toggles are opt-in — a user who has never configured them
// keeps the extra columns hidden (false). `hppdIncludeTrainees` defaults on so DHPPD/NHPPD count all
// direct-care staff (BIPA "all hours" intent) until a user explicitly narrows it. Named + shared so
// the provider mapping and both usePrintout fallback paths agree instead of duplicating literals.
export const DEFAULT_SHOW_DHPPD = false;
export const DEFAULT_SHOW_NHPPD = false;
export const DEFAULT_SHOW_ACTUAL_STAFF_COLUMN = false;
export const DEFAULT_SHOW_ACTUAL_HOURS_COLUMN = false;
export const DEFAULT_HPPD_INCLUDE_TRAINEES = true;

const defaultSettings: StaffingSettings = {
	groupRoles: false,
	weeklyGroupingType: WeeklyGroupingType.Role,
	disableTimeGroupsOverlaps: false,
	overlapShifts: false,
	view: WeeklyViewOption.V2,
	dailyView: DailyViewOption.standard,
	printNotes: true,
	printGaps: true,
	showRoleGroupStats: false,
	divideGroupsByPage: false,
	separateSheetsBy: DailyPrintoutGroupingOption.NONE,
	showPtoSheet: false,
	showCallOutsSheet: false,
	dailyPrintoutGrouping: [DailyPrintoutGroupingOption.ROLE],
	censusByLocation: LocationMode.None,
	spaceBetweenGroups: true,
	printoutOrientation: PrintoutOrientation.Landscape,
	printoutFitToPage: true,
	printoutFitToWidth: 1,
	printoutFitToHeight: 0,
	hideHppdNursingHours: false,
	printoutColumnWidths: undefined,
	flagNotOnboarded: true,
	flagAgency: true,
	mergeSimilarShiftTimes: false,
	alignGroupTimesToPopular: false,
	// DEFAULT: Detailed printout customization is opt-in so existing saved and unsaved printouts
	// retain their current columns, headings, and staff-name cells until a user enables each switch.
	customTimeGroups: { enabled: false, groups: [] },
	appendRoleToName: false,
	hideColumnNames: false,
	appendEmptyColumns: 0,
	customColumnNames: { enabled: false, labels: {} },
	customLocationOrder: { enabled: false, orderedLocationNames: [] },
	customSheetTabs: { tabs: [] },
	showAdmins: DEFAULT_SHOW_ADMINS,
	showLocationCensus: false,
	showDhppd: DEFAULT_SHOW_DHPPD,
	showNhppd: DEFAULT_SHOW_NHPPD,
	showActualStaffColumn: DEFAULT_SHOW_ACTUAL_STAFF_COLUMN,
	showActualHoursColumn: DEFAULT_SHOW_ACTUAL_HOURS_COLUMN,
	hppdIncludeTrainees: DEFAULT_HPPD_INCLUDE_TRAINEES,
	hppdSeparationType: HppdSeparationType.None,
	hideHppdDailyView: false,
	wantedMode: undefined,
	hiddenLocations: [],
	// Management Center defaults (match user_settings server defaults)
	mcLaborCost: false,
	mcIncludeHolidays: false,
	mcIncludeLateScheduled: false,
	mcOtMode: "actual",
	// DEFAULT: on, matching the `mc_estimate_missing_wage` server default (APP-731), so MC costs here
	// estimate the same way the day view, staffing panels and cost predictions always have.
	// This value is read by TWO different situations, not just the obvious one:
	//   1. a user whose settings query never runs (a non-head-nurse) — for them this IS the final answer;
	//   2. ANY user, head nurses included, during the window before the settings query resolves.
	// (2) is why consumers must not fire setting-parameterized requests until `settingsResolved` is true:
	// an ungated request in that window is computed from these guesses and then corrected. APP-731
	// backfilled `mc_estimate_missing_wage` so this one now matches what users have stored, but the
	// other four `mc_*` settings were not, so the window still matters. See `useManagementCenterFilters`.
	mcEstimateMissingWage: true,
	// Reached only via the `!userSettings` branch below — i.e. whenever `userSettings` is undefined,
	// which covers both a user whose settings query never runs (a non-head-nurse) and the pre-resolution
	// window for everyone else. Unlike the mapped branch these have no `??` fallback to fall back TO,
	// so they need a value here.
	weekScheduleExport: DEFAULT_PERSISTED_EXPORT_SETTINGS,
	staffingMonthlyExport: DEFAULT_PERSISTED_MONTHLY_SETTINGS,
	staffingWeeklyExport: DEFAULT_WEEKLY_SETTINGS,
	jobColors: {},
};

/**
 * When the job management feature is on the product groups exclusively by job, so we force weekly
 * grouping to "Jobs" regardless of what the user previously saved. HPPD separation is normalized
 * either way (see `normalizeHppdSeparation`). This keeps the weekly gaps view and daily view consistent and
 * lets a later settings submit persist the corrected values.
 */
const applyJobManagementGrouping = (base: StaffingSettings, jobManagementEnabled: boolean): StaffingSettings => ({
	...base,
	weeklyGroupingType: jobManagementEnabled ? WeeklyGroupingType.Jobs : base.weeklyGroupingType,
	hppdSeparationType: normalizeHppdSeparation(base.hppdSeparationType, jobManagementEnabled),
});

export const UserSettingsProvider: FC<{ children: ReactNode }> = ({ children }) => {
	// `isLoading` (TanStack v5) is `isPending && isFetching` — true only while the FIRST fetch is in
	// flight, and false for a query that never runs. `useUserSettings` is disabled for non-head-nurses,
	// so reading `isPending` here instead would leave them permanently unresolved.
	const { data: userSettings, isLoading: isLoadingUserSettings } = useUserSettings();
	const settingsResolved = !isLoadingUserSettings;
	const { mutateAsync: updateUserSettingsMutation, isPending: isLoading } = useUpdateUserSettings();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const settings = useMemo<StaffingSettings>(() => {
		if (!userSettings) {
			return applyJobManagementGrouping(defaultSettings, jobManagementEnabled);
		}

		return applyJobManagementGrouping(
			{
				groupRoles: userSettings.group_roles,
				weeklyGroupingType: (userSettings.weekly_grouping_type as WeeklyGroupingType) ?? WeeklyGroupingType.Role,
				disableTimeGroupsOverlaps: userSettings.disable_time_groups_overlaps ?? false,
				overlapShifts: userSettings.overlap_shifts,
				view: userSettings.weekly_view_version as WeeklyViewOption,
				dailyView: userSettings.daily_view_type as DailyViewOption,
				printNotes: userSettings.print_notes,
				printGaps: userSettings.print_gaps,
				showRoleGroupStats: userSettings.show_role_group_stats ?? false,
				divideGroupsByPage: userSettings.divide_groups_by_page,
				separateSheetsBy: (userSettings.separate_sheets_by as DailyPrintoutGroupingOption) || DailyPrintoutGroupingOption.NONE,
				showPtoSheet: userSettings.show_pto_sheet ?? false,
				showCallOutsSheet: userSettings.show_call_outs_sheet ?? false,
				dailyPrintoutGrouping: (userSettings.daily_printout_grouping_list as DailyPrintoutGroupingOption[]) ?? [DailyPrintoutGroupingOption.ROLE],
				censusByLocation: userSettings.census_by_location,
				printoutSortBy: userSettings.printout_sort_by,
				printoutColumns: userSettings.printout_columns,
				staffPerRow: userSettings.staff_per_row ?? 1,
				rowGroupingBy: userSettings.row_grouping_by ?? [],
				columnGroupingBy: userSettings.column_grouping_by ?? null,
				spaceBetweenGroups: userSettings.printout_space_between_groups ?? true,
				includeTimeOffReason: userSettings.printout_include_time_off_reason ?? true,
				printoutOrientation: userSettings.printout_orientation ?? PrintoutOrientation.Landscape,
				printoutFitToPage: userSettings.printout_fit_to_page ?? DEFAULT_PRINTOUT_FIT_TO_PAGE,
				printoutFitToWidth: userSettings.printout_fit_to_width ?? DEFAULT_PRINTOUT_FIT_TO_WIDTH,
				printoutFitToHeight: userSettings.printout_fit_to_height ?? DEFAULT_PRINTOUT_FIT_TO_HEIGHT,
				hideHppdNursingHours: userSettings.printout_hide_hppd_nursing_hours ?? false,
				printoutColumnWidths: userSettings.printout_column_widths ?? undefined,
				flagNotOnboarded: userSettings.printout_flag_not_onboarded ?? true,
				flagAgency: userSettings.printout_flag_agency ?? true,
				mergeSimilarShiftTimes: userSettings.printout_merge_similar_shift_times ?? false,
				alignGroupTimesToPopular: userSettings.printout_align_group_times_to_popular ?? false,
				customTimeGroups: userSettings.printout_custom_time_groups,
				appendRoleToName: userSettings.printout_append_role_to_name,
				hideColumnNames: userSettings.printout_hide_column_names,
				appendEmptyColumns: userSettings.printout_append_empty_columns,
				customColumnNames: userSettings.printout_custom_column_names,
				customLocationOrder: userSettings.custom_location_order ?? { enabled: false, orderedLocationNames: [] },
				customSheetTabs: userSettings.custom_sheet_tabs ?? { tabs: [] },
				showAdmins: userSettings.show_admins ?? DEFAULT_SHOW_ADMINS,
				showLocationCensus: userSettings.show_location_census ?? false,
				showDhppd: userSettings.printout_show_dhppd ?? DEFAULT_SHOW_DHPPD,
				showNhppd: userSettings.printout_show_nhppd ?? DEFAULT_SHOW_NHPPD,
				showActualStaffColumn: userSettings.printout_show_actual_staff_column ?? DEFAULT_SHOW_ACTUAL_STAFF_COLUMN,
				showActualHoursColumn: userSettings.printout_show_actual_hours_column ?? DEFAULT_SHOW_ACTUAL_HOURS_COLUMN,
				hppdIncludeTrainees: userSettings.printout_hppd_include_trainees ?? DEFAULT_HPPD_INCLUDE_TRAINEES,
				hppdSeparationType: (userSettings.hppd_separation_type as HppdSeparationType) ?? HppdSeparationType.None,
				hideHppdDailyView: userSettings.hide_hppd_daily_view ?? false,
				wantedMode: (userSettings.wanted_mode as StatsMode) ?? undefined,
				hiddenLocations: userSettings.hidden_locations ?? [],
				mcLaborCost: userSettings.mc_labor_cost ?? false,
				mcIncludeHolidays: userSettings.mc_include_holidays ?? false,
				mcIncludeLateScheduled: userSettings.mc_include_late_scheduled ?? false,
				mcOtMode: (userSettings.mc_ot_mode as OtMode) ?? "actual",
				// DEFAULT: on, matching the column's server default (APP-731) — reached only if the API omits
				// the field entirely. A stored value still wins; APP-731 backfilled those to true as well.
				mcEstimateMissingWage: userSettings.mc_estimate_missing_wage ?? true,
				// No `??` on purpose: the column is NOT NULL with a server default, so a fallback here
				// could only mask a genuine backend contract break instead of surfacing it.
				weekScheduleExport: userSettings.week_staff_schedule_export_settings,
				staffingMonthlyExport: userSettings.staffing_monthly_export_settings,
				staffingWeeklyExport: userSettings.staffing_weekly_export_settings,
				jobColors: userSettings.job_colors,
			},
			jobManagementEnabled,
		);
	}, [userSettings, jobManagementEnabled]);

	const updateSettings = useCallback(
		async (newSettings: Partial<StaffingSettings>, saveAsDefault?: boolean, options?: { includePrintoutPresets: boolean }): Promise<void> => {
			const updatedSettings: StaffingSettings = { ...settings, ...newSettings };

			const backendSettings: UpdateUserSettingsRequest = {
				group_roles: newSettings.groupRoles,
				weekly_grouping_type: newSettings.weeklyGroupingType,
				disable_time_groups_overlaps: newSettings.disableTimeGroupsOverlaps,
				overlap_shifts: newSettings.overlapShifts,
				weekly_view_version: newSettings.view,
				daily_view_type: newSettings.dailyView,
				print_notes: newSettings.printNotes,
				print_gaps: newSettings.printGaps,
				showRoleGroupStats: newSettings.showRoleGroupStats,
				divideGroupsByPage: newSettings.divideGroupsByPage,
				separateSheetsBy: newSettings.separateSheetsBy,
				showPtoSheet: newSettings.showPtoSheet,
				showCallOutsSheet: newSettings.showCallOutsSheet,
				daily_printout_grouping: newSettings.dailyPrintoutGrouping?.[0],
				// Only set the secondary grouping when grouping is actually part of this update; otherwise
				// leave it undefined (filtered out) so partial saves don't clobber the stored value with NONE.
				secondary_daily_printout_grouping: newSettings.dailyPrintoutGrouping
					? (newSettings.dailyPrintoutGrouping[1] ?? DailyPrintoutGroupingOption.NONE)
					: undefined,
				daily_printout_grouping_list: newSettings.dailyPrintoutGrouping,
				printoutSortBy: newSettings.printoutSortBy,
				printoutColumns: newSettings.printoutColumns,
				staffPerRow: newSettings.staffPerRow,
				rowGroupingBy: newSettings.rowGroupingBy,
				columnGroupingBy: newSettings.columnGroupingBy,
				spaceBetweenGroups: newSettings.spaceBetweenGroups,
				includeTimeOffReason: newSettings.includeTimeOffReason,
				printoutOrientation: newSettings.printoutOrientation,
				printoutFitToPage: newSettings.printoutFitToPage,
				printoutFitToWidth: newSettings.printoutFitToWidth,
				printoutFitToHeight: newSettings.printoutFitToHeight,
				hideHppdNursingHours: newSettings.hideHppdNursingHours,
				printoutColumnWidths: newSettings.printoutColumnWidths,
				flagNotOnboarded: newSettings.flagNotOnboarded,
				flagAgency: newSettings.flagAgency,
				mergeSimilarShiftTimes: newSettings.mergeSimilarShiftTimes,
				alignGroupTimesToPopular: newSettings.alignGroupTimesToPopular,
				customTimeGroups: newSettings.customTimeGroups,
				appendRoleToName: newSettings.appendRoleToName,
				hideColumnNames: newSettings.hideColumnNames,
				appendEmptyColumns: newSettings.appendEmptyColumns,
				customColumnNames: newSettings.customColumnNames,
				customLocationOrder: newSettings.customLocationOrder,
				customSheetTabs: newSettings.customSheetTabs,
				showAdmins: newSettings.showAdmins,
				showLocationCensus: newSettings.showLocationCensus,
				showDhppd: newSettings.showDhppd,
				showNhppd: newSettings.showNhppd,
				showActualStaffColumn: newSettings.showActualStaffColumn,
				showActualHoursColumn: newSettings.showActualHoursColumn,
				hppdIncludeTrainees: newSettings.hppdIncludeTrainees,
				censusByLocation: newSettings.censusByLocation,
				hppdSeparationType: newSettings.hppdSeparationType,
				hideHppdDailyView: newSettings.hideHppdDailyView,
				wantedMode: newSettings.wantedMode,
				hidden_locations: newSettings.hiddenLocations,
				mcLaborCost: newSettings.mcLaborCost,
				mcIncludeHolidays: newSettings.mcIncludeHolidays,
				mcIncludeLateScheduled: newSettings.mcIncludeLateScheduled,
				mcOtMode: newSettings.mcOtMode,
				mcEstimateMissingWage: newSettings.mcEstimateMissingWage,
				weekStaffScheduleExportSettings: newSettings.weekScheduleExport,
				staffingMonthlyExportSettings: newSettings.staffingMonthlyExport,
				staffingWeeklyExportSettings: newSettings.staffingWeeklyExport,
				jobColors: newSettings.jobColors,
				saveAsDefault,
				// Undefined (so left out of the request) unless the caller opts in.
				includePrintoutPresets: options?.includePrintoutPresets,
			};

			await updateUserSettingsMutation(backendSettings);

			trackEvent("staffing_settings_toggled", { settings: updatedSettings });
		},
		[settings, updateUserSettingsMutation],
	);

	return <UserSettingsContext.Provider value={{ settings, updateSettings, isLoading, settingsResolved }}>{children}</UserSettingsContext.Provider>;
};
